import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PLATFORM_TAGS, PLATFORM_TAG_COPY, type CreateFeatureFlagBody } from '@/types/feature-flag';

const RESERVED_KEYS = ['count', 'documents', 'birthdayPromptCadenceDays', 'favoritesEnabled'];

const formSchema = z.object({
  key: z
    .string()
    .min(1, 'Key is required')
    .max(64, 'Key must be 64 characters or fewer')
    .regex(
      /^[a-zA-Z][a-zA-Z0-9_]*$/,
      'Must start with a letter and use only letters, digits, or underscores'
    )
    .refine(value => !RESERVED_KEYS.includes(value), {
      message: 'That key is reserved by the app config payload',
    }),
  label: z
    .string()
    .trim()
    .min(1, 'Label is required')
    .max(128, 'Label must be 128 characters or fewer'),
  description: z.string().max(2000, 'Description must be 2000 characters or fewer'),
  platformTag: z.enum(PLATFORM_TAGS),
});

type FormValues = z.infer<typeof formSchema>;

const EMPTY: FormValues = {
  key: '',
  label: '',
  description: '',
  platformTag: 'both',
};

interface FlagCreateDialogProps {
  open: boolean;
  scopeLabel: string;
  isSubmitting: boolean;
  onSubmit: (body: CreateFeatureFlagBody) => void;
  onClose: () => void;
}

export function FlagCreateDialog({
  open,
  scopeLabel,
  isSubmitting,
  onSubmit,
  onClose,
}: FlagCreateDialogProps) {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(formSchema), defaultValues: EMPTY });

  useEffect(() => {
    if (open) reset(EMPTY);
  }, [open, reset]);

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        // Cancel is disabled while submitting; Escape and the overlay must not
        // be a way around that, or a failed create loses everything typed.
        if (!next && !isSubmitting) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create {scopeLabel} flag
          </DialogTitle>
          <DialogDescription>
            The key is what clients read from the app config payload. A newly created flag has no
            effect until a client ships code that reads it.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(values =>
            onSubmit({
              key: values.key,
              label: values.label,
              description: values.description.trim() || null,
              platformTag: values.platformTag,
            })
          )}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="flag-key">Key *</Label>
            <Input
              id="flag-key"
              placeholder="e.g. groupOrderingEnabled"
              aria-invalid={errors.key ? true : undefined}
              aria-describedby={errors.key ? 'flag-key-error' : undefined}
              {...register('key')}
            />
            {errors.key && (
              <p id="flag-key-error" className="text-xs text-destructive">
                {errors.key.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="flag-label">Label *</Label>
            <Input
              id="flag-label"
              placeholder="e.g. Group Ordering"
              aria-invalid={errors.label ? true : undefined}
              aria-describedby={errors.label ? 'flag-label-error' : undefined}
              {...register('label')}
            />
            {errors.label && (
              <p id="flag-label-error" className="text-xs text-destructive">
                {errors.label.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="flag-description">Description</Label>
            <Textarea
              id="flag-description"
              rows={3}
              placeholder="What this flag controls, and what happens when it is off."
              aria-invalid={errors.description ? true : undefined}
              aria-describedby={errors.description ? 'flag-description-error' : undefined}
              {...register('description')}
            />
            {errors.description && (
              <p id="flag-description-error" className="text-xs text-destructive">
                {errors.description.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="flag-platform-tag">Affects</Label>
            <Controller
              control={control}
              name="platformTag"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="flag-platform-tag">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLATFORM_TAGS.map(tag => (
                      <SelectItem key={tag} value={tag}>
                        {PLATFORM_TAG_COPY[tag].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <p className="text-xs text-muted-foreground">
              A label for operators only. It does not change who receives the flag.
            </p>
          </div>

          <p className="text-xs text-muted-foreground">
            Platform and version overrides are set from the flag list after creation — a newly
            created flag is always advisory, since nothing reads it yet.
          </p>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create flag
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
